import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar } from "lucide-react";
import { formatDate, CategoryBadge } from "@/lib/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LatestPosts() {
    const [latest, setLatest] = useState([]);

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/posts`, { params: { exclude_category: "news" } }).then(({ data }) => {
            if (alive) setLatest((data || []).slice(0, 3));
        }).catch(() => {});
        return () => { alive = false; };
    }, []);

    if (!latest.length) return null;

    return (
        <section data-testid="latest-posts-section" className="py-24 lg:py-32 bg-white border-y border-acre-line">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-5">
                        <div className="text-[11px] uppercase tracking-[0.28em] text-acre-secondary mb-5">
                            From the Blog
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl tracking-tight leading-[1.05] text-acre-secondary">
                            Field notes from our practice.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end justify-between gap-6">
                        <p className="text-acre-muted text-base lg:text-lg leading-relaxed max-w-xl">
                            Practical, plain-spoken thinking on the questions families and closely-held businesses bring through our door.
                        </p>
                        <Link
                            to="/blog"
                            data-testid="latest-posts-view-all"
                            className="hidden md:inline-flex items-center gap-2 text-sm text-acre-primary hover:gap-3 transition-all shrink-0"
                        >
                            All articles <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {latest.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/blog/${p.slug}`}
                            data-testid={`latest-post-${p.slug}`}
                            className="group bg-acre-bg border border-acre-line overflow-hidden flex flex-col hover:-translate-y-1 transition-all"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden bg-acre-line">
                                <img
                                    src={p.image}
                                    alt={p.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                            <div className="p-7 flex-1 flex flex-col">
                                <CategoryBadge category={p.category} />
                                <h3 className="font-serif-display text-2xl text-acre-ink leading-snug mt-4 group-hover:text-acre-secondary transition-colors">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-sm leading-relaxed text-acre-muted flex-1">
                                    {(p.excerpt || "").length > 140 ? p.excerpt.slice(0, 140) + "…" : p.excerpt}
                                </p>
                                <div className="mt-5 pt-4 border-t border-acre-line flex items-center gap-2 text-xs text-acre-muted">
                                    <Calendar size={12} /> {formatDate(p.date)}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-10 md:hidden">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-acre-primary">
                        All articles <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
